<template>
  <div
    class="c-list"
    :class="[{ hideInfo: hidePageInfor }, { hideImage: getInsightType === 'media-mention' }]"
  >
    <div class="c-list__heading">
      <topic-filter
        v-if="showFilter"
        label="Filter"
        :topics="filterTopics"
        :selectedTopics="data.selectedTopics"
        :pageInfo="pageInfo()"
        @toggleTopic="toggleTopic"
        @clearTopics="clearTopics"
      />
      <page-info :pageInfo="pageInfo()" class="page-info" />
    </div>
    <div class="c-list-main">
      <div
        class="c-list-container"
        :class="[
          { hasfeatured: !loading && featuredItem != null },
          { noSignUp: rendering?.placeholders['jss-list-sign-up']?.length === 0 }
        ]"
      >
        <template v-if="!loading && featuredItem">
          <items
            :items="[featuredItem]"
            :topicImageSrc="topicImageSrc"
            :useDateIssued="fields.useDateIssued.value"
            :badges="badges"
            :badgeSize="fields.badgeSize?.value"
            :selectedTopics="data.selectedTopics"
            class="featured-only"
          />
        </template>
        <template v-if="!loading && pageInfo().page === 1 && data.selectedTopics.length === 0">
          <sc-placeholder name="jss-list-sign-up" :rendering="rendering" />
        </template>

        <template v-if="!loading">
          <items
            :items="items"
            :topicImageSrc="topicImageSrc"
            :useDateIssued="fields.useDateIssued.value"
            :badges="badges"
            :badgeSize="fields.badgeSize?.value"
            :selectedTopics="data.selectedTopics"
            :featuredIndex="featuredItemIndex"
          />
          <pagination
            v-if="!loading"
            class="c-page-mobile"
            :pageInfo="pageInfo()"
            @setPage="setPage"
          />
        </template>
        <div v-else class="c-list-results">Please wait...</div>
      </div>
      <div
        class="c-list-right-rail"
        :class="[{ addShadow: isRightRailFilled }, { hide: hideRightRail }]"
      >
        <sc-placeholder name="jss-list-right-rail" :rendering="rendering" />
      </div>
    </div>
    <pagination v-if="!loading" :pageInfo="pageInfo()" @setPage="setPage" />
  </div>
</template>

<script>
import { getCurrentInstance, reactive } from 'vue';
import { InsightsQuery } from './CIS-Resource-PagedListing.graphql';
import { useQuery } from '@vue/apollo-composable';
import { Placeholder } from '@sitecore-jss/sitecore-jss-vue';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-vue';
import TopicFilter from './CIS-Resource-PagedListing-Filter.vue';
import PageInfo from './CIS-Resource-PagedListing-PageInfo';
import Items from './CIS-Resource-PagedListing-Items.vue';
import Pagination from './CIS-Resource-PagedListing-Pagination.vue';
import config from '../../../temp/config';
import { formatGuidWithDashes, formatGuidWithoutDashes } from '../../../lib/guid';

// Compiled Regex to format a GUID without dashes or curley braces.
const guidRegex = /[{}-]/g;
const normalizeGuid = (guid) => {
  return guid.replace(guidRegex, '').toUpperCase();
};

export default {
  name: 'CIS-Resource-PagedListing',
  components: {
    ScPlaceholder: Placeholder,
    TopicFilter,
    PageInfo,
    Items,
    Pagination
  },
  props: {
    fields: {
      type: Object,
      default: () => ({})
    },
    rendering: {
      type: Object
    }
  },
  data() {
    return {
      isRightRailFilled: false
    };
  },
  setup(props) {
    // Array of options for the Filter child component. Use a non-reactive variable for
    // topics and owners since they only need to be set once, on the first load of the GraphQL results.
    const filterTopics = [];
    const owners = {};
    const instance = getCurrentInstance();
    const properties = instance.appContext.config.globalProperties.$jss;
    const routeQuery = instance.appContext.app.config.globalProperties.$route?.query;
    // Convert Sitecore's route.query object into a valid search string
    // usable by a URLSearchParams object
    const initSearchString = Object.keys(routeQuery)
      .map((key) => {
        return Array.isArray(routeQuery[key])
          ? routeQuery[key].map((value) => `${key}=${value}`)
          : `${key}=${routeQuery[key]}`;
      })
      .flat()
      .join('&');

    // Get page size from Sitecore data source or default to 10
    const pageSize = isNaN(props?.fields?.pageSize?.value)
      ? 10
      : Number.parseInt(props.fields.pageSize.value);

    // Use the source path if available, otherwise use the page's path.
    const rootItem = `/sitecore/content/cisecurity/ngen/home${
      props?.fields?.source?.url || properties.sitecoreContext()?.itemPath
    }`;

    const sortBy = props?.fields?.sortByName?.value
      ? 'sortableResourceTitle'
      : !props?.fields?.useDateIssued?.value
      ? 'publishedAt'
      : 'dateIssued';
    // If sorting by name then sort acending, otherwise sort decending
    const sortDesc = !props?.fields?.sortByName?.value;

    const getTopicsByNames = (topicNames, topicGroup) => {
      // Force topicNames to be an array if not already.
      // Include 'topics' with the '-owner' suffix for backwards compatability with existing URLs.
      const topics = Array.isArray(topicNames) ? topicNames : [topicNames] || [];
      topics.push(
        ...(Array.isArray(topicNames) ? topicNames : [topicNames])?.map((t) => `${t}-owner`)
      );

      // Find topicIds from the props with matching names
      const topicIds = props.fields.topics
        .filter((t) => topics.includes(t.name))
        .map((t) => formatGuidWithoutDashes(t.id));

      return topicIds.map((t) => {
        return {
          id: t,
          group: topicGroup
        };
      });
    };

    const getOwnerById = (id) => {
      const getItemById = (id, items) => {
        if (!Array.isArray(items)) {
          throw new Error("The 'items' parameter must be an array.");
        }

        for (let item of items) {
          if (item && normalizeGuid(item.id) === normalizeGuid(id)) {
            return item;
          }

          if (item && item.children?.length > 0) {
            const childItem = getItemById(id, item.children);
            if (childItem) return childItem;
          }
        }

        // Return null if the id is not found.
        return null;
      };

      return getItemById(id, owners?.children ?? []);
    };

    let initSelectedTopics = getTopicsByNames(routeQuery?.topic, 'Solutions');
    initSelectedTopics.push(...getTopicsByNames(routeQuery?.solutions, 'Solutions'));
    initSelectedTopics.push(...getTopicsByNames(routeQuery?.industry, 'Industry'));

    const data = reactive({
      rootItem: rootItem,
      page: parseInt(routeQuery?.page) || 1,
      pageSize: pageSize,
      sortBy: sortBy,
      sortDesc: sortDesc,
      selectedTopics: initSelectedTopics || []
    });

    const variables = () => {
      const groupMap = data.selectedTopics?.reduce((acc, topic) => {
        const newTopicIds = [topic.id];
        const topicChildren = getOwnerById(topic.id)?.children;
        if (topicChildren?.length > 0) {
          topicChildren.forEach((t) => newTopicIds.push(t.id));
        }

        if (!acc[topic.group]) {
          acc[topic.group] = newTopicIds;
        } else {
          acc[topic.group].push(...newTopicIds);
        }

        return acc;
      }, {});

      const fieldsEqual =
        Object.keys(groupMap).length > 0
          ? Object.keys(groupMap).map((group) => ({
              name: 'owners',
              value: groupMap[group].join(',')
            }))
          : null;

      const output = {
        rootItem: rootItem,
        pageSize: pageSize,
        sortBy: sortBy,
        sortDesc: sortDesc,
        after: ((data.page - 1) * data.pageSize).toString(),
        fieldsEqual: fieldsEqual
      };

      return output;
    };

    const queryOptions = () => {
      return {
        prefetch: true
      };
    };

    const { result, loading, error, onResult } = useQuery(InsightsQuery, variables, queryOptions);

    onResult((result) => {
      // If the set 'page' is out of bounds then reset page to the last available page.
      if (
        !result.loading &&
        data.page > 1 &&
        result?.data?.search?.results?.pageInfo?.endCursor === '0' &&
        result?.data?.search?.results?.pageInfo?.startCursor === '0'
      ) {
        data.page = Math.ceil(result.data?.search?.results?.totalCount / data.pageSize) || 1;
      }

      if (!result.loading) {
        // Initialize array of filter topics only if it isn't already initialized
        if (filterTopics.length === 0) {
          // Get list of topic Ids from component's data source
          const enabledTopicIds = props.fields.topics.map((t) => t.id);

          // Loop thru the groups of topics ('Solutions', 'Industry', etc) to build
          // data set of options for the Filter child component.
          const getEnabledTopicItems = (topicItems) => {
            const output = [];
            topicItems?.forEach((topicItem) => {
              if (enabledTopicIds.includes(formatGuidWithDashes(topicItem.id).toLowerCase())) {
                output.push(topicItem);
              }
              if (topicItem.hasChildren) {
                output.push(...getEnabledTopicItems(topicItem.children));
              }
            });

            return output;
          };

          result.data.owners.children.forEach((topicGroup) => {
            const enabledTopicItems = getEnabledTopicItems(topicGroup.children);
            // Only add this group of filter topics if it has enabled topic items
            if (enabledTopicItems && enabledTopicItems.length > 0) {
              filterTopics.push({
                id: topicGroup.id,
                path: topicGroup.path,
                name: topicGroup.name,
                children: enabledTopicItems
              });
            }
          });
        }

        // Initialize array of owners only if it isn't already initialized
        if (!owners?.id) {
          Object.assign(owners, result.data?.owners);
        }
      }
    });

    return {
      data,
      result,
      owners,
      filterTopics,
      loading,
      error,
      getTopicsByNames,
      getOwnerById,
      initSearchString
    };
  },
  mounted() {
    const url = new URL(window.location);
    const page = Number.parseInt(url?.searchParams?.get('page'));
    const hash = url?.hash;
    if (page > 1) {
      this.data.page = page;
    }
    const params = hash ? new URLSearchParams(hash.substring(1)) : url?.searchParams;
    let initSelectedTopics = this.getTopicsByNames(params?.getAll('topic'), 'Solutions');
    initSelectedTopics.push(...this.getTopicsByNames(params?.getAll('solutions'), 'Solutions'));
    initSelectedTopics.push(...this.getTopicsByNames(params?.getAll('industry'), 'Industry'));
    this.data.selectedTopics = initSelectedTopics;
    this.setRightRailFilled();
    this.setRightRailFilterMessage(this.showFilter ? 'block' : 'none');
  },
  updated() {
    if (!this.loading) {
      this.setUrlQueryString();
      this.setRightRailList();
    }
  },
  methods: {
    pageInfo() {
      // Supplies object to PageInfo child component
      return {
        page: this.data.page,
        pages: this.pages,
        startItemIndex: this.startItemIndex,
        endItemIndex: this.endItemIndex,
        totalCount: this.result?.search?.results?.totalCount,
        hasPreviousPage: this.hasPreviousPage,
        hasNextPage: this.hasNextPage,
        initSearchString: this.initSearchString
      };
    },
    setPage(page) {
      this.data.page = page;

      // Find the top of the main content area, adjusting for the header and banner.
      // The document and window objects aren't supported in node's SSR. The setPage function
      // can be called by apollo's update function to handle search page being out of range,
      // but in that case the scrollTo isn't needed.
      if (document && window) {
        let top =
          document.getElementsByClassName('c-site__main')[0]?.offsetTop -
            (document.getElementsByClassName('c-banner__content')[0]?.offsetHeight || 0) || 220;
        window.scrollTo(0, top);
      }
    },
    toggleTopic(topicId, topicGroup) {
      // find topic object by its id in the list of selected topics
      const topicIdIndex = this.data.selectedTopics.findIndex((t) => t.id === topicId);

      // Reset to page 1
      this.data.page = 1;

      // if found, remove the object
      if (topicIdIndex >= 0) {
        this.data.selectedTopics.splice(topicIdIndex, 1);
      } else {
        // else, add the object
        this.data.selectedTopics.push({
          id: topicId,
          group: topicGroup
        });
      }
    },
    clearTopics() {
      this.data.page = 1;
      this.data.selectedTopics = [];
    },
    setUrlQueryString() {
      // If this page/component is running in the experience editor then exit
      if (isEditorActive() === true) {
        return;
      }

      // The window object isn't supported in Node's SSR, but this is OK since
      // it isn't called for SSR.
      const url = new URL(window.location);
      const currentSearchParams = new URLSearchParams(window.location.search);
      const newSearchParams = new URLSearchParams(window.location.search);

      // Reset page and topic parameters
      newSearchParams.delete('page');
      // Topic is no longer being used; replaced by 'solutions' and 'industry'
      newSearchParams.delete('topic');
      // Get the list of possible 'groups' of topics and delete them from the params
      this.owners?.children?.forEach((topicGroup) => {
        newSearchParams.delete(topicGroup?.name?.toLowerCase());
      });

      if (this.data.page > 1) {
        newSearchParams.set('page', this.data.page);
      }
      // Create copy of selectedTopicsIds array and sort it so the query string
      // values appear in the same order regardless of in what order the topics were selected.
      [...this.data.selectedTopics].sort().forEach((topic) => {
        const owner = this.getOwnerById(topic.id);
        if (owner) {
          newSearchParams.append(topic.group.toLowerCase(), owner.name);
        }
      });

      // Sort the search params so they are in the same order when compared.
      currentSearchParams.sort();
      newSearchParams.sort();

      // Only push onto the history if the new params are different from the current params.
      if (newSearchParams.toString() !== currentSearchParams.toString()) {
        url.search = `?${newSearchParams.toString()}`;
        url.hash = '';
        window.history.pushState({}, '', url);
      }
    },
    setRightRailFilterMessage(display) {
      if (typeof display !== 'string' || display.length == 0) {
        return;
      }
      const filterInfo = document.querySelector('.c-filter-info-default');
      if (filterInfo && 'style' in filterInfo) {
        filterInfo.style.display = display;
      }
    },
    setRightRailFilled() {
      if (document && document.querySelector('.c-list-right-rail')) {
        var sideBarContent = document
          .querySelector('.c-list-right-rail')
          ?.innerHTML?.replace(new RegExp('<!---->', 'g'), '');
        this.isRightRailFilled = sideBarContent != '';
      }
    },
    setRightRailList() {
      // The document object isn't supported in Node's SSR, but this function never called in SSR.
      document
        .querySelectorAll(`.c-list-right-rail .c-filter-info`)
        .forEach((i) => (i.style.display = 'none'));

      if (this.showFilter) {
        this.setRightRailFilterMessage('block');
      }

      this.data.selectedTopics.forEach((s) => {
        const topicName = this.fields.topics.find((t) => normalizeGuid(t.id) === s.id)?.name;
        if (topicName) {
          const rightRailItem = document.querySelector(
            `.c-list-right-rail .c-filter-info.${topicName}`
          );
          if (rightRailItem) {
            this.setRightRailFilterMessage('none');
            rightRailItem.style.display = 'block';
          }
        }
      });
    },
    topicImageSrc(topics) {
      // topics can be a pipe delimited list
      const topicId = topics?.split('|')[0];
      const topic = this.filterTopics.find((t) => t.id.replaceAll('-', '') === topicId) ?? 'CIS';

      let imageSrc = topic?.fields?.defaultImage?.value?.src;
      if (imageSrc && !imageSrc.toLowerCase().startsWith('http')) {
        // Only prefix with the sitecoreApiHost if the protocol/host name is missing from the imageSrc
        imageSrc = config.sitecoreApiHost + imageSrc;
      }

      return {
        src:
          imageSrc ||
          `${config.sitecoreApiHost}/-/media/Project/cisecurity/cisecurity/data/media/img/thumbnail-cis`,
        alt: topic?.fields?.defaultImage?.value?.alt || 'CIS'
      };
    }
  },
  computed: {
    items() {
      return this.result?.search?.results?.items;
    },
    pages() {
      return Math.ceil(this.result?.search?.results?.totalCount / this.data.pageSize);
    },
    startItemIndex() {
      return Number.parseInt(this.result?.search?.results?.pageInfo?.startCursor) || 0;
    },
    endItemIndex() {
      return Number.parseInt(this.result?.search?.results?.pageInfo?.endCursor) || 0;
    },
    hasPreviousPage() {
      return this.result?.search?.results?.pageInfo?.hasPreviousPage || false;
    },
    hasNextPage() {
      return this.result?.search?.results?.pageInfo?.hasNextPage || false;
    },
    badges() {
      return this?.fields?.badges.map((badge) => formatGuidWithoutDashes(badge.id));
    },
    showFilter() {
      return !!this?.fields?.showFilterByTopic?.value && this.fields?.topics?.length > 0;
    },
    hideRightRail() {
      return this?.fields?.hideRightRail?.value;
    },
    hidePageInfor() {
      return this?.fields?.hidePageInfor?.value;
    },
    getInsightType() {
      return this.data?.rootItem?.split('\/')?.pop() || '';
    },
    featuredItem() {
      // Filter items by templateName and find the first with featuredStory === 1
      return (
        this.items
          ?.filter((item) => item.templateName === 'CIS-Resource-Newsletter-RouteType')
          .find((item) => item?.item?.featuredStory?.value === '1') || null
      );
    },
    featuredItemIndex() {
      const featuredItemIndex = this.items.findIndex(
        (item) =>
          item.templateName === 'CIS-Resource-Newsletter-RouteType' &&
          item?.item?.featuredStory?.value === '1'
      );

      return featuredItemIndex;
    }
  }
};
</script>

<style lang="scss" scoped>
.c-list {
  @include padding-y(2rem, 0);

  .c-list__heading {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    column-gap: 5rem;
    @include padding-x-thin;
  }

  &.hideInfo {
    .page-info {
      display: none;
    }
  }

  &.hideImage {
    &:deep() {
      .c-list-image {
        display: none;
      }
    }
  }
}

.c-list-main {
  width: 100%;
  @include padding-x-thin;
  padding-bottom: 4rem;

  @include desktop-up {
    display: flex;
    padding-top: 3rem;
    padding-bottom: 2rem;
  }
}

.c-list-container {
  width: 100%;

  .c-list-results {
    min-height: 50rem;
    @include desktop-up {
      padding-right: 5rem;
    }

    &.featured-only {
      min-height: auto;
    }

    &.is-featured {
      background-color: red;
    }
  }

  &.hasfeatured {
    .featured-only {
      margin-bottom: 2.4rem;
    }

    &.noSignUp {
      .featured-only {
        margin-bottom: 6.2rem;
      }
    }
  }
}

.c-list-right-rail {
  @include desktop-up {
    width: 21%;
    margin-left: 7rem;
    margin-top: 0;
    padding-top: 0;
    border-top: none;
    flex-shrink: 0;
  }

  &.hide {
    display: none;
  }

  &.addShadow {
    @include tablet-down {
      margin: 4rem 0 0 0 !important;
      max-width: 100% !important;
    }

    @include desktop-down {
      @include add-Shadow;
      padding: 2rem;
    }
  }
}
</style>
